import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { promptTemplateApi } from "@/apis/promptTemplate";
import Router from "@/constants/router";
import { PromptTemplateDto, PromptTemplateListRequestDto } from "@/dto/PromptTemplateDto";
import { ResultListDto } from "@/dto/ResponseDto";

/**
 * 보스베이비 프롬프트 템플릿 목록 커스텀 훅
 *
 * @description 보스베이비 채팅 프롬프트 템플릿 목록의 비즈니스 로직을 관리하는 훅
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-29
 */
export default function useBossBabyPromptTemplateList() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<ResultListDto<PromptTemplateDto> | null>(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  // 🔥 보스베이비 채팅 프롬프트만 필터링 (templateKey = 'boss_baby_chat')
  const fetchData = useCallback(async (params: PromptTemplateListRequestDto = {}) => {
    setLoading(true);
    try {
      const result = await promptTemplateApi.getList({
        page: params.page || 1,
        limit: params.limit || 20,
        templateKey: 'boss_baby_chat', // 🔥 보스베이비 채팅 프롬프트만 조회
        category: params.category || selectedCategory || undefined,
        search: params.search || searchKeyword || undefined,
      });
      setData(result);
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to fetch prompt templates", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchKeyword, enqueueSnackbar]);

  const fetchCategories = useCallback(async () => {
    try {
      const cats = await promptTemplateApi.getCategories();
      // 보스베이비 카테고리만 필터링
      const bossBabyCategories = cats.filter((cat) => cat === 'boss_baby');
      setCategories(bossBabyCategories);
    } catch (error: any) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, [fetchData, fetchCategories]);

  const handleCategoryChange = useCallback((category: string) => {
    setSelectedCategory(category);
    fetchData({ category: category || undefined });
  }, [fetchData]);

  const handleSearch = useCallback((keyword: string) => {
    setSearchKeyword(keyword);
    fetchData({ search: keyword || undefined });
  }, [fetchData]);

  const handleCreate = useCallback(() => {
    navigate(Router.BossBabyPromptTemplateCreate);
  }, [navigate]);

  const handleDetail = useCallback((templateId: number) => {
    navigate(Router.BossBabyPromptTemplateDetail.replace(":id", templateId.toString()));
  }, [navigate]);

  const handleDelete = useCallback(async (templateId: number) => {
    if (!confirm("Are you sure you want to delete this prompt template?")) return;
    try {
      await promptTemplateApi.delete(templateId);
      enqueueSnackbar("Prompt template deleted successfully.", { variant: "success" });
      fetchData();
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to delete prompt template", { variant: "error" });
    }
  }, [fetchData, enqueueSnackbar]);

  return {
    data,
    loading,
    categories,
    selectedCategory,
    searchKeyword,
    handleCategoryChange,
    handleSearch,
    handleCreate,
    handleDetail,
    handleDelete,
  };
}









